import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import {
  formatListeningCreatedBy,
  formatListeningDateTime,
  formatListeningDeviceId,
  formatListeningLocation,
  formatListeningNumber,
  formatListeningNumberWithUnit,
  getListeningCurrentColor,
  sortListeningFrequencies,
} from "./listeningViewUtils";
import {
  LISTENING_DISPLAY_UNITS,
  convertSplForDisplay,
  hlToSpl,
} from "./listeningUnits";

const EMPTY_MAP = {};
const DESCRIPTION_PREVIEW_LINE_HEIGHT_EM = 1.43;
const DESCRIPTION_PREVIEW_MAX_LINES = 10;
const ERROR_HIGHLIGHT_THRESHOLD_DB = 5.5;

function getSummaryMap(listening, key) {
  return listening?.[key] ?? listening?.listeningSummary?.[key] ?? EMPTY_MAP;
}

function getNoStimulusMap(listening) {
  return listening?.listeningConditions?.noStimulusByFrequency ?? EMPTY_MAP;
}

function getListeningTableValues(listening) {
  return (
    listening?.listeningTable?.valuesByFrequency ??
    listening?.listeningTable ??
    EMPTY_MAP
  );
}

function getNestedValue(map, rowKey, columnKey) {
  const row = map?.[rowKey] ?? map?.[String(rowKey)] ?? null;
  if (!row) {
    return null;
  }

  return row?.[columnKey] ?? row?.[String(columnKey)] ?? null;
}

function getAverageAbsoluteError(listening) {
  return (
    listening?.averageAbsoluteError ??
    listening?.listeningSummary?.averageAbsoluteError ??
    null
  );
}

function getStatusLabel(current) {
  if (current === true) {
    return "Aktualny";
  }

  if (current === false) {
    return "Nieaktualny";
  }

  return null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function getExpectedListeningValue({
  frequency,
  intensity,
  isBluetoothSpeaker = false,
}) {
  const numericIntensity = toNumberOrNull(intensity);
  if (numericIntensity === null) {
    return null;
  }

  return isBluetoothSpeaker
    ? numericIntensity
    : hlToSpl(Number(frequency), numericIntensity);
}

function shouldIgnoreBecauseOfListeningConditions(noStimulus, expectedValue) {
  return (
    noStimulus !== null && expectedValue !== null && noStimulus >= expectedValue
  );
}

export function shouldHighlightMeasuredValue({
  frequency,
  intensity,
  measuredValue,
  noStimulusByFrequency,
  isBluetoothSpeaker = false,
}) {
  const measured = toNumberOrNull(measuredValue);
  if (measured === null) {
    return false;
  }

  const expectedValue = getExpectedListeningValue({
    frequency,
    intensity,
    isBluetoothSpeaker,
  });
  if (expectedValue === null) {
    return false;
  }

  const noStimulus = toNumberOrNull(
    noStimulusByFrequency?.[frequency] ??
      noStimulusByFrequency?.[String(frequency)] ??
      null,
  );

  if (shouldIgnoreBecauseOfListeningConditions(noStimulus, expectedValue)) {
    return false;
  }

  return Math.abs(measured - expectedValue) >= ERROR_HIGHLIGHT_THRESHOLD_DB;
}

function collectFrequencies(listening) {
  return sortListeningFrequencies([
    ...(listening?.frequencies || []),
    ...Object.keys(getNoStimulusMap(listening)),
    ...Object.keys(getSummaryMap(listening, "minErrorByFrequency")),
    ...Object.keys(getSummaryMap(listening, "maxErrorByFrequency")),
    ...Object.keys(getSummaryMap(listening, "averageErrorByFrequency")),
    ...Object.keys(getListeningTableValues(listening)),
  ]);
}

function collectIntensities(listening) {
  const explicitIntensities = sortListeningFrequencies(
    listening?.intensities || [],
  );
  if (explicitIntensities.length) {
    return explicitIntensities;
  }

  const tableValues = getListeningTableValues(listening);
  return sortListeningFrequencies(
    Object.values(tableValues).flatMap((row) => Object.keys(row || {})),
  );
}

function MetadataRow({ listening }) {
  return (
    <Box display="flex" flexWrap="wrap" mt={1}>
      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Data: {formatListeningDateTime(listening?.createdAt)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Twórca: {formatListeningCreatedBy(listening?.createdBy)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Device ID: {formatListeningDeviceId(listening?.deviceId)}
        </Typography>
      </Box>

      <Box mr={3} mb={1}>
        <Typography variant="body2" color="textSecondary">
          Lokalizacja: {formatListeningLocation(listening?.location)}
        </Typography>
      </Box>
    </Box>
  );
}

function ExpandableDescription({ value }) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);

  const displayValue =
    value !== undefined && value !== null && String(value) !== ""
      ? String(value)
      : "-";

  useEffect(() => {
    const element = textRef.current;
    if (!element || expanded) {
      setIsExpandable(false);
      return;
    }

    const checkOverflow = () => {
      if (!element) {
        return;
      }

      setIsExpandable(element.scrollHeight > element.clientHeight + 1);
    };

    checkOverflow();

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(checkOverflow);
    }
  }, [displayValue, expanded]);

  return (
    <Box mt={1}>
      <Typography
        ref={textRef}
        variant="body2"
        style={{
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          tabSize: 4,
          lineHeight: `${DESCRIPTION_PREVIEW_LINE_HEIGHT_EM}em`,
          overflow: "hidden",
          maxHeight: expanded
            ? "none"
            : `${
                DESCRIPTION_PREVIEW_LINE_HEIGHT_EM *
                DESCRIPTION_PREVIEW_MAX_LINES
              }em`,
        }}
      >
        {displayValue}
      </Typography>

      {!expanded && isExpandable && (
        <Button
          size="small"
          color="primary"
          onClick={() => setExpanded(true)}
          style={{
            marginTop: 4,
            padding: 0,
            minWidth: "auto",
            textTransform: "none",
          }}
        >
          Więcej...
        </Button>
      )}
    </Box>
  );
}

function HeaderSection({ listening, isByModel, onOpenComponents }) {
  const statusLabel = getStatusLabel(listening?.current);
  const averageAbsoluteError = getAverageAbsoluteError(listening);

  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <Box mr={2} mb={2} style={{ minWidth: 280, flex: 1 }}>
          <Typography variant="h6">
            {listening?.title || "Bez tytułu"}
          </Typography>

          <MetadataRow listening={listening} />

          <ExpandableDescription value={listening?.description} />
        </Box>

        <Box display="flex" flexDirection="column" alignItems="flex-end" mb={2}>
          {isByModel && statusLabel ? (
            <Chip
              label={statusLabel}
              style={{
                marginBottom: 8,
                backgroundColor: getListeningCurrentColor(listening?.current),
                color: "#fff",
              }}
            />
          ) : null}

          <Typography variant="body2" color="textSecondary">
            Średnia bezwzględna różnica
          </Typography>

          <Typography variant="subtitle2">
            {formatListeningNumberWithUnit(averageAbsoluteError, "dB")}
          </Typography>

          {isByModel ? (
            <Box mt={2}>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<LayersIcon />}
                onClick={onOpenComponents}
                disabled={!listening?.id}
              >
                Składowe kalibracje
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
}

function ConditionsSection({ listening }) {
  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
      <Box display="flex" flexWrap="wrap">
        <Box mr={4} mb={1}>
          <Typography variant="body2" color="textSecondary">
            Ogólny poziom tła:{" "}
            {formatListeningNumber(
              listening?.listeningConditions?.generalBackgroundIntensity,
            )}{" "}
            dB
          </Typography>
        </Box>

        <Box mr={4} mb={1}>
          <Typography variant="body2" color="textSecondary">
            Błąd sonometru:{" "}
            {formatListeningNumber(
              listening?.listeningConditions?.sonometerError,
            )}{" "}
            dB
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function DetailedTableSection({ listening, displayUnit, isBluetoothSpeaker }) {
  const frequencies = collectFrequencies(listening);
  const intensities = collectIntensities(listening);
  const noStimulusByFrequency = getNoStimulusMap(listening);
  const tableValues = getListeningTableValues(listening);
  const minErrorByFrequency = getSummaryMap(listening, "minErrorByFrequency");
  const maxErrorByFrequency = getSummaryMap(listening, "maxErrorByFrequency");
  const averageErrorByFrequency = getSummaryMap(
    listening,
    "averageErrorByFrequency",
  );
  const targetUnit = isBluetoothSpeaker
    ? LISTENING_DISPLAY_UNITS.SPL
    : LISTENING_DISPLAY_UNITS.HL;

  if (!frequencies.length) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Box px={2} py={1}>
        <Typography variant="caption" color="textSecondary">
          Wartości zmierzone [dB {displayUnit}]. Poziomy docelowe w nagłówku
          są zapisane w dB {targetUnit}.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Częstotliwość</TableCell>
            <TableCell align="center">
              Bez bodźca [dB {displayUnit}]
            </TableCell>
            {intensities.map((intensity) => (
              <TableCell key={`intensity-${intensity}`} align="center">
                {intensity} dB {targetUnit}
              </TableCell>
            ))}
            <TableCell align="center">Min błąd</TableCell>
            <TableCell align="center">Max błąd</TableCell>
            <TableCell align="center">Średni błąd</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {frequencies.map((frequency) => (
            <TableRow key={`row-${frequency}`}>
              <TableCell>{frequency} Hz</TableCell>

              <TableCell align="center">
                {formatListeningNumber(
                  convertSplForDisplay(
                    frequency,
                    noStimulusByFrequency?.[frequency] ??
                      noStimulusByFrequency?.[String(frequency)] ??
                      null,
                    displayUnit,
                  ),
                )}
              </TableCell>

              {intensities.map((intensity) => {
                const measuredValue = getNestedValue(
                  tableValues,
                  frequency,
                  intensity,
                );

                const shouldHighlight = shouldHighlightMeasuredValue({
                  frequency,
                  intensity,
                  measuredValue,
                  noStimulusByFrequency,
                  isBluetoothSpeaker,
                });

                return (
                  <TableCell
                    key={`cell-${frequency}-${intensity}`}
                    align="center"
                    style={
                      shouldHighlight
                        ? { color: "#d32f2f", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {formatListeningNumber(
                      convertSplForDisplay(
                        frequency,
                        measuredValue,
                        displayUnit,
                      ),
                    )}
                  </TableCell>
                );
              })}

              <TableCell align="center">
                {formatListeningNumber(
                  minErrorByFrequency?.[frequency] ??
                    minErrorByFrequency?.[String(frequency)] ??
                    null,
                )}
              </TableCell>

              <TableCell align="center">
                {formatListeningNumber(
                  maxErrorByFrequency?.[frequency] ??
                    maxErrorByFrequency?.[String(frequency)] ??
                    null,
                )}
              </TableCell>

              <TableCell align="center">
                {formatListeningNumber(
                  averageErrorByFrequency?.[frequency] ??
                    averageErrorByFrequency?.[String(frequency)] ??
                    null,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ListeningDetailedView({
  listening,
  loading,
  error,
  isByModel,
  onOpenComponents,
  displayUnit = LISTENING_DISPLAY_UNITS.HL,
  isBluetoothSpeaker = false,
}) {
  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!listening) {
    return (
      <Typography color="textSecondary">
        Nie udało się wczytać szczegółów odsłuchu.
      </Typography>
    );
  }

  return (
    <Box>
      <HeaderSection
        listening={listening}
        isByModel={isByModel}
        onOpenComponents={onOpenComponents}
      />
      <ConditionsSection listening={listening} />
      <DetailedTableSection
        listening={listening}
        displayUnit={displayUnit}
        isBluetoothSpeaker={isBluetoothSpeaker}
      />
    </Box>
  );
}
